import { useQuery, UseQueryOptions, keepPreviousData } from '@tanstack/react-query';
import {
  CorporateActionType,
  CorporateActionStatus,
  type ListCorporateActionsResponse,
  type CorporateAction,
  type CorporateActionOrderBy,
} from '@/features/tokenized-equity/grpc/types';

type UseFetchCorporateActionsOptions = {
  page_size?: number;
  page_token?: string;
  action_types?: CorporateActionType[];
  statuses?: CorporateActionStatus[];
  order_by?: CorporateActionOrderBy;
  cbrn?: string;
  enabled?: boolean;
  queryOptions?: Omit<
    UseQueryOptions<ListCorporateActionsResponse, Error>,
    'queryKey' | 'queryFn' | 'enabled'
  >;
};

/**
 * Convert string enum name to numeric enum value for CorporateActionType
 */
function parseActionTypeString(actionType: string | number): CorporateActionType {
  if (typeof actionType === 'number') return actionType;

  switch (actionType) {
    case 'CORPORATE_ACTION_TYPE_UNSPECIFIED':
      return CorporateActionType.CORPORATE_ACTION_TYPE_UNSPECIFIED;
    case 'CORPORATE_ACTION_TYPE_CASH_DIVIDEND':
      return CorporateActionType.CORPORATE_ACTION_TYPE_CASH_DIVIDEND;
    case 'CORPORATE_ACTION_TYPE_STOCK_DIVIDEND':
      return CorporateActionType.CORPORATE_ACTION_TYPE_STOCK_DIVIDEND;
    case 'CORPORATE_ACTION_TYPE_STOCK_SPLIT':
      return CorporateActionType.CORPORATE_ACTION_TYPE_STOCK_SPLIT;
    case 'CORPORATE_ACTION_TYPE_REVERSE_STOCK_SPLIT':
      return CorporateActionType.CORPORATE_ACTION_TYPE_REVERSE_STOCK_SPLIT;
    default:
      return CorporateActionType.CORPORATE_ACTION_TYPE_UNSPECIFIED;
  }
}

/**
 * Convert string enum name to numeric enum value for CorporateActionStatus
 */
function parseActionStatusString(status: string | number): CorporateActionStatus {
  if (typeof status === 'number') return status;

  switch (status) {
    case 'CORPORATE_ACTION_STATUS_UNSPECIFIED':
      return CorporateActionStatus.CORPORATE_ACTION_STATUS_UNSPECIFIED;
    case 'CORPORATE_ACTION_STATUS_CREATED':
      return CorporateActionStatus.CORPORATE_ACTION_STATUS_CREATED;
    case 'CORPORATE_ACTION_STATUS_ANNOUNCED':
      return CorporateActionStatus.CORPORATE_ACTION_STATUS_ANNOUNCED;
    case 'CORPORATE_ACTION_STATUS_COMPLETED':
      return CorporateActionStatus.CORPORATE_ACTION_STATUS_COMPLETED;
    case 'CORPORATE_ACTION_STATUS_CANCELED':
      return CorporateActionStatus.CORPORATE_ACTION_STATUS_CANCELED;
    default:
      return CorporateActionStatus.CORPORATE_ACTION_STATUS_UNSPECIFIED;
  }
}

/**
 * Normalize corporate action response to ensure enum values are numbers
 */
function normalizeCorporateActionsResponse(
  response: ListCorporateActionsResponse,
): ListCorporateActionsResponse {
  return {
    ...response,
    corporate_actions: response.corporate_actions.map(
      (action): CorporateAction => ({
        ...action,
        action_type: parseActionTypeString(action.action_type as string | number),
        state: parseActionStatusString(action.state as string | number),
      }),
    ),
  };
}

/**
 * Query key factory for corporate actions
 * Follows project convention for consistent query key management
 */
export const QUERY_KEY_CORPORATE_ACTIONS = (params: {
  page_size?: number;
  page_token?: string;
  action_types?: CorporateActionType[];
  statuses?: CorporateActionStatus[];
  order_by?: CorporateActionOrderBy;
  cbrn?: string;
}) => [
  'corporateActions',
  params.page_size,
  params.page_token ?? 'first_page',
  params.action_types,
  params.statuses,
  params.order_by,
  params.cbrn,
];

/**
 * React Query hook for fetching corporate actions from the Tangor Connector API
 *
 * Returns raw gRPC response. Use formatter utilities from
 * `@/features/tokenized-equity/components/explore/equity-details/utils/corporateActionsFormatter`
 * to convert to frontend display format.
 *
 * @example
 * ```tsx
 * const { data } = useFetchCorporateActions({
 *   page_size: 50,
 *   page_token: 'optional-token',
 * });
 * ```
 */
export function useFetchCorporateActions({
  page_size = 50,
  page_token,
  action_types,
  statuses,
  order_by,
  cbrn,
  enabled = true,
  queryOptions,
}: UseFetchCorporateActionsOptions = {}) {
  return useQuery<ListCorporateActionsResponse, Error>({
    queryKey: QUERY_KEY_CORPORATE_ACTIONS({
      page_size,
      page_token,
      action_types,
      statuses,
      order_by,
      cbrn,
    }),
    async queryFn() {
      // Call API
      const params = new URLSearchParams();
      if (page_size) params.set('page_size', String(page_size));
      if (page_token) params.set('page_token', page_token);
      if (action_types) params.set('action_types', action_types.join(','));
      if (statuses) params.set('statuses', statuses.join(','));
      if (order_by !== undefined) params.set('order_by', String(order_by));
      if (cbrn) params.set('security_cbrn', cbrn);

      const response = await fetch(`/api/v1/corporateActions?${params}`);

      if (!response.ok) {
        let errorMsg = `HTTP error ${response.status}`;
        try {
          const errorData = (await response.json()) as { error?: string };
          if (errorData?.error) {
            errorMsg = errorData.error;
          }
        } catch {
          // ignore json parse error, keep default errorMsg
        }
        throw new Error(errorMsg);
      }

      const rawData = (await response.json()) as ListCorporateActionsResponse;

      // Normalize string enum values to numeric enum values
      return normalizeCorporateActionsResponse(rawData);
    },
    enabled,
    staleTime: 60_000, // 1 minute
    placeholderData: keepPreviousData,
    ...queryOptions,
  });
}
